import { Box, Grid } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import UPButton from 'components/button';
import UPCard from 'components/card';
import { UPDataGrid } from 'components/data-grid';
import UPInput from 'components/input';
import UPPagination from 'components/pagination';
import UPToggle from 'components/toggle/UPToggle';
import { CompanyRole } from 'constants/CompanyRole';
import { toNumber } from 'lodash';
import { generateNumberOfPages } from 'modules/employees/helpers';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { clearCompanyAction, getCompaniesAction, getOnboardingCompaniesAction } from 'store/actions/company-actions';
import { clearCompanyContactAction } from 'store/actions/company-contact-actions';
import { removeAllCostCentersAction } from 'store/actions/company-cost-centers-actions';
import { clearCompanyFinancialAction } from 'store/actions/company-financial-actions';
import { removeAllFunctionsAction } from 'store/actions/company-functions-actions';
import { removeAllLocationsAction } from 'store/actions/company-locations-actions';
import { removeAllManagersAction } from 'store/actions/company-managers-actions';
import { clearCompanyWageExemptionsAction } from 'store/actions/company-wage-exemptions-actions';
import { clearBicAction } from 'store/actions/financial-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import { clearCompanyPayrollAction } from 'store/actions/payroll-actions';
import { companiesSelector, onboardingCompaniesSelector } from 'store/selectors/company-selectors';
import { activeMenuItemSelector } from 'store/selectors/menu-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  COMPANIES__ADD_NEW_COMPANY,
  COMPANY__CLIENTS_TOGGLE_OPTION,
  COMPANY__NO_COMPANIES_MATCH_SEARCH,
  COMPANY__NO_COMPANIES_YET,
  COMPANY__ONBOARDING_TOGGLE_OPTION,
  COMPANY_INFORMATION__COMMERCIAL_NAME,
  EMPLOYEES__STATUS,
  GENERAL__EDIT,
  GENERAL__EMAIL,
  GENERAL__FIRST_NAME,
  GENERAL__LAST_NAME,
  GENERAL__NAME,
  GENERAL__PHONE_NUMBER,
  GENERAL__SEARCH,
  MENU__COMPANIES,
  ONBOARDING__AWAITING_APPROVAL,
  ONBOARDING__ONBOARDING,
  ONBOARDING__READY_FOR_MARKUP,
} from 'translations/constants';
import { ICompanyGridDetails, IOnboardingCompanyGridDetails } from 'types/company';
import { escapeRegExp, getFormattedCompanyAddress } from 'utils/helpers';
import { ReactComponent as CompanyLogo } from '../../assets/icons/CompanyLogo.svg';
import { CompanyAddNewItemButton, ToggleContainer } from './styles';

const Companies = (): JSX.Element => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchText, setSearchText] = useState('');
  const [rowOnHover, setRowOnHover] = useState<number>();
  const [searchMatchCompanies, setSearchMatchCompanies] = useState<ICompanyGridDetails[]>([]);
  const [searchMatchOnboardingCompanies, setSearchMatchOnboardingCompanies] = useState<IOnboardingCompanyGridDetails[]>(
    [],
  );
  const [currentPageCompanies, setCurrentPageCompanies] = useState<ICompanyGridDetails[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  const [numberOfPages, setNumberOfPages] = useState<number>(1);
  const [selectedOption, setSelectedOption] = useState<number>(1);

  const companies = useAppSelector(companiesSelector);
  const onboardingCompanies = useAppSelector(onboardingCompaniesSelector);
  const activeItem = useAppSelector(activeMenuItemSelector);
  const userRole = useAppSelector(profileSelector).role;

  useEffect(() => {
    if (activeItem !== t(MENU__COMPANIES)) {
      dispatch(setMenuStatusAction(t(MENU__COMPANIES)));
    }
  }, [activeItem, dispatch, t]);

  useEffect(() => {
    dispatch(getCompaniesAction());
    dispatch(getOnboardingCompaniesAction());
    dispatch(clearCompanyAction());
    dispatch(clearCompanyContactAction());
    dispatch(removeAllLocationsAction());
    dispatch(removeAllManagersAction());
    dispatch(clearCompanyPayrollAction());
    dispatch(removeAllCostCentersAction());
    dispatch(removeAllFunctionsAction());
    dispatch(clearCompanyWageExemptionsAction());
    dispatch(clearCompanyFinancialAction());
    dispatch(clearBicAction());
  }, [dispatch]);

  useEffect(() => {
    setSearchMatchCompanies(companies);
    setSearchMatchOnboardingCompanies(onboardingCompanies);
    setSearchText('');
  }, [selectedOption, companies, onboardingCompanies]);

  useEffect(() => {
    if (searchMatchCompanies) {
      const auxNumber = generateNumberOfPages(itemsPerPage, searchMatchCompanies.length);
      setNumberOfPages(auxNumber);
      const start = (currentPage - 1) * itemsPerPage;
      setCurrentPageCompanies(searchMatchCompanies.slice(start, start + itemsPerPage));
    }
  }, [searchMatchCompanies, currentPage, itemsPerPage]);

  const displayCompanies = useCallback(() => {
    if (currentPageCompanies.length > 0) {
      return currentPageCompanies.map(c => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={c.Id}>
          <UPCard
            title={c.Name}
            subTitle={c.CommercialName}
            description={getFormattedCompanyAddress(c.Address)}
            onClick={() => navigate(`/company/${c.Id}/general`)}
            logo={c.Logo}
            defaultLogo={<CompanyLogo />}
          />
        </Grid>
      ));
    }
    if (selectedOption === 1 && companies.length === 0) {
      return <Box padding="15px">{t(COMPANY__NO_COMPANIES_YET)}</Box>;
    }
    if (selectedOption === 1 && companies.length !== 0) {
      return <Box padding="15px">{t(COMPANY__NO_COMPANIES_MATCH_SEARCH)}</Box>;
    }
    return <Box />;
  }, [companies, currentPageCompanies, navigate, t, selectedOption]);

  const onboardingCompaniesGridColumns: GridColDef[] = [
    { field: 'name', headerName: t(GENERAL__NAME), width: 200 },
    { field: 'commercialName', headerName: t(COMPANY_INFORMATION__COMMERCIAL_NAME), width: 200, sortable: false },
    { field: 'companyIdNumber', headerName: 'ID', width: 100, sortable: false },
    { field: 'contactFirstName', headerName: t(GENERAL__FIRST_NAME), width: 150, sortable: false },
    { field: 'contactLastName', headerName: t(GENERAL__LAST_NAME), width: 150, sortable: false },
    { field: 'contactEmail', headerName: t(GENERAL__EMAIL), width: 300, sortable: false },
    { field: 'contactPhoneNumber', headerName: t(GENERAL__PHONE_NUMBER), width: 150, sortable: false },
    {
      field: 'status',
      headerName: t(EMPLOYEES__STATUS),
      headerAlign: 'center',
      align: 'center',
      width: 250,
      valueGetter: params => {
        switch (params.row.status) {
          case 1:
            return t(ONBOARDING__ONBOARDING);
          case 2:
            return t(ONBOARDING__READY_FOR_MARKUP);
          default:
            return t(ONBOARDING__AWAITING_APPROVAL);
        }
      },
      sortComparator: (v1, v2) => {
        const possibleValues = [
          t(ONBOARDING__ONBOARDING),
          t(ONBOARDING__READY_FOR_MARKUP),
          t(ONBOARDING__AWAITING_APPROVAL),
        ];
        return possibleValues.indexOf(String(v1)) - possibleValues.indexOf(String(v2));
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        if (rowOnHover === params.id && params.row.status === 3) {
          return (
            <UPButton
              onClick={() => navigate(`/company/${params.row.createdCompanyId}/general`)}
              text={t(GENERAL__EDIT)}
              outlined
            />
          );
        }
        return null;
      },
    },
  ];

  const displayOnboardingCompanies = () => {
    return (
      <Box height="80vh" width="100%" padding="15px" sx={{ backgroundColor: 'white' }}>
        <UPDataGrid
          columns={onboardingCompaniesGridColumns}
          rows={searchMatchOnboardingCompanies}
          setRowOnHover={setRowOnHover}
          getRowId={row => row.companyIdNumber}
          withCheckboxes={false}
          disableSelectionOnClick
        />
      </Box>
    );
  };

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue);
    if (searchValue) {
      const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
      const filteredCompanyEntries = companies.filter((companyEntry: ICompanyGridDetails) => {
        return Object.keys(companyEntry).some((field: string) => {
          return (
            searchRegex.test(companyEntry[field]) || searchRegex.test(getFormattedCompanyAddress(companyEntry.Address))
          );
        });
      });
      const filteredOnboardingCompanyEntries = onboardingCompanies.filter(
        (companyEntry: IOnboardingCompanyGridDetails) => {
          return Object.keys(companyEntry).some((field: string) => {
            return searchRegex.test(companyEntry[field]);
          });
        },
      );
      setSearchMatchCompanies(filteredCompanyEntries);
      setSearchMatchOnboardingCompanies(filteredOnboardingCompanyEntries);
    } else {
      setSearchMatchCompanies(companies);
      setSearchMatchOnboardingCompanies(onboardingCompanies);
    }
  };

  const handlePageChange = (e: any, value: number) => {
    setCurrentPage(value);
  };

  const handleOptionChange = (value: number) => {
    setSelectedOption(value);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {userRole !== CompanyRole.ACCOUNT_MANAGER && (
          <CompanyAddNewItemButton
            text={`+ ${t(COMPANIES__ADD_NEW_COMPANY)}`}
            onClick={() => navigate('/company/new/general')}
          />
        )}
        <Box
          sx={{
            width: '300px',
            alignContent: 'center',
          }}
        >
          <UPInput
            value={searchText}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              requestSearch(event.target.value);
            }}
            label=""
            placeholder={`${t(GENERAL__SEARCH)}...`}
            icon="search"
          />
        </Box>
        <ToggleContainer>
          <UPToggle
            optionOne={t(COMPANY__CLIENTS_TOGGLE_OPTION)}
            optionTwo={t(COMPANY__ONBOARDING_TOGGLE_OPTION)}
            selectedOption={selectedOption}
            onChange={handleOptionChange}
          />
        </ToggleContainer>
      </Box>
      <Grid container spacing="10px" width="100%" marginTop="1px">
        {selectedOption === 1 ? displayCompanies() : displayOnboardingCompanies()}
      </Grid>
      {searchMatchCompanies.length > 0 && selectedOption === 1 && (
        <UPPagination
          itemsPerPage={itemsPerPage}
          onChange={e => {
            setItemsPerPage(toNumber(e.target.value));
            setCurrentPage(1);
          }}
          numberOfPages={numberOfPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      )}
    </Box>
  );
};

export default Companies;
